import { useNodes } from '@/hooks/useNodes'
import type { Node } from '@/api/types'
import NodeCard from '@/components/NodeCard'
import NodeStats from '@/components/NodeStats'
import GPUPanel from '@/components/GPUPanel'
import { Card, EmptyState } from '@/admin-design/_common'

export default function Dashboard() {
  const { data: nodes, isLoading, isError, isFetching } = useNodes()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">
            Infrastructure Overview
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Docker nodes and containers across all servers
          </p>
        </div>
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            refreshing...
          </span>
        )}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Card key={i} className="h-64 animate-pulse">
              <div className="px-4 py-3 border-b border-border">
                <div className="h-5 w-32 bg-muted rounded" />
              </div>
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="h-8 bg-muted rounded" />
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="border border-destructive-soft rounded-lg bg-destructive-soft p-4">
          <p className="text-sm text-destructive-soft-foreground">
            Failed to load nodes. Check that the backend is running.
          </p>
        </div>
      )}

      {/* Node grid */}
      {!isLoading && !isError && nodes && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {nodes.map((node: Node) => (
            <div key={node.id} className="space-y-3">
              <NodeCard node={node} />
              <NodeStats nodeName={node.name} />
              {node.name === 'gpu' && <GPUPanel />}
            </div>
          ))}
          {nodes.length === 0 && (
            <Card className="col-span-full">
              <EmptyState
                title="No nodes registered"
                description="Add nodes via the backend API."
              />
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
